/**
 * MARKET COMPS GENERATOR
 * Generates representative comparable rentals based on market data
 * Use this until you integrate real RentCast API comps
 */

/**
 * Generate representative market comps
 * @param {Object} marketData - Market data with median, range, sample size
 * @param {number} bedrooms - Number of bedrooms
 * @param {number} userSqft - User's property square footage (optional, for highlighting)
 * @returns {Array} Array of comparable rental objects
 */
function generateRepresentativeComps(marketData, bedrooms, userSqft = null) {
  const median = marketData.medianRent;
  const lowRange = marketData.lowRange;
  const highRange = marketData.highRange;
  
  // Generate 8-10 comps distributed around the median
  const comps = [];
  
  // Distribution: 2 high, 3 median, 3 low
  const pricePoints = [
    { rent: Math.round(median * 1.08), tier: 'high' },   // +8%
    { rent: Math.round(median * 1.04), tier: 'high' },   // +4%
    { rent: median, tier: 'median' },                     // median
    { rent: median, tier: 'median' },                     // median
    { rent: median, tier: 'median' },                     // median
    { rent: Math.round(median * 0.96), tier: 'low' },    // -4%
    { rent: Math.round(median * 0.92), tier: 'low' },    // -8%
    { rent: Math.round(median * 0.88), tier: 'low' }     // -12%
  ];
  
  // Expected sqft by bedroom count
  const baseSqft = {
    0: 550,   // Studio
    1: 750,   // 1BR
    2: 950,   // 2BR
    3: 1250,  // 3BR
    4: 1600   // 4BR
  }[bedrooms] || 950;
  
  // Expected bathrooms
  const expectedBaths = {
    0: 1,
    1: 1,
    2: 1,
    3: 2,
    4: 2.5
  }[bedrooms] || 1;
  
  pricePoints.forEach(point => {
    const comp = {
      rent: point.rent,
      sqft: calculateSqft(baseSqft, point.tier),
      bathrooms: calculateBathrooms(expectedBaths, point.tier, bedrooms),
      features: generateFeatures(point.tier, bedrooms),
      isSimilar: userSqft ? Math.abs(calculateSqft(baseSqft, point.tier) - userSqft) < 100 : false
    };
    
    comps.push(comp);
  });
  
  // Sort by price descending
  comps.sort((a, b) => b.rent - a.rent);
  
  return comps;
}

/**
 * Calculate square footage based on tier
 */
function calculateSqft(baseSqft, tier) {
  const variations = {
    high: Math.round(baseSqft * 1.08),    // Larger
    median: baseSqft,                      // Average
    low: Math.round(baseSqft * 0.92)      // Smaller
  };
  
  return variations[tier] || baseSqft;
}

/**
 * Calculate bathrooms based on tier
 */
function calculateBathrooms(expectedBaths, tier, bedrooms) {
  if (tier === 'high' && bedrooms >= 2) {
    return expectedBaths + 0.5; // Add half bath for high tier
  }
  return expectedBaths;
}

/**
 * Generate feature descriptions based on tier
 */
function generateFeatures(tier, bedrooms) {
  const features = {
    high: [
      'Renovated, parking, in-unit laundry',
      'Updated kitchen/bath, parking',
      'Recently renovated, modern finishes',
      'Excellent condition, all amenities'
    ],
    median: [
      'Good condition, parking',
      'Standard condition, in-building laundry',
      'Well-maintained, pet-friendly',
      'Good condition, dishwasher'
    ],
    low: [
      'Fair condition',
      'Needs minor updates',
      'Standard condition',
      'Fair condition, no parking'
    ]
  };
  
  const tierFeatures = features[tier] || features.median;
  return tierFeatures[Math.floor(Math.random() * tierFeatures.length)];
}

/**
 * Format comp for display
 * @param {Object} comp - Comp object
 * @returns {string} Formatted string for display
 */
function formatComp(comp) {
  return `$${comp.rent.toLocaleString()} • ${comp.sqft} sqft • ${comp.bathrooms}BA • ${comp.features}`;
}

/**
 * Get comps summary statistics
 * @param {Array} comps - Array of comps
 * @returns {Object} Summary stats
 */
function getCompsSummary(comps) {
  const rents = comps.map(c => c.rent);
  const median = rents.sort((a, b) => a - b)[Math.floor(rents.length / 2)];
  const min = Math.min(...rents);
  const max = Math.max(...rents);
  
  return {
    median,
    min,
    max,
    count: comps.length
  };
}

// Export for use in other modules
export { generateRepresentativeComps, formatComp, getCompsSummary };
